import React, { useState, useEffect } from 'react';
import { savePatientHistory, updatePatientHistory, viewPatientHistory } from '../services/historyService';
import Swal from 'sweetalert2';

const PatientHistoryModal = ({ patientId, doctorId, show, onClose }) => {
  const [historyData, setHistoryData] = useState({
    PastSurgicalhistory: '',
    PastMedicalhistory: '',
    Drughistory: '',
    GyneorOBShistory: ''
  });
  
  const [existingHistoryId, setExistingHistoryId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  // Load existing history when modal opens
  useEffect(() => {
    if (show && patientId) {
      loadPatientHistory();
    }
  }, [show, patientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPatientHistory = async () => {
    if (!patientId) return;
    
    setInitialLoad(true);
    try {
      const response = await viewPatientHistory(patientId);
      if (response.success) {
        if (response.hasExistingData && response.data) {
          // History exists - populate form fields
          const historyRecord = response.data;
          setHistoryData({
            PastSurgicalhistory: historyRecord.PastSurgicalhistory || '',
            PastMedicalhistory: historyRecord.PastMedicalhistory || '',
            Drughistory: historyRecord.Drughistory || '',
            GyneorOBShistory: historyRecord.GyneorOBShistory || ''
          });
          setExistingHistoryId(historyRecord.Id);
        } else {
          // No existing history - clear form for new entry
          setHistoryData({
            PastSurgicalhistory: '',
            PastMedicalhistory: '',
            Drughistory: '',
            GyneorOBShistory: ''
          });
          setExistingHistoryId(null);
        }
      } else {
        // API error - show error message
        console.error('API Error:', response.message);
      }
    } catch (error) {
      console.error('Error loading patient history:', error);
    } finally {
      setInitialLoad(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setHistoryData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!patientId || !doctorId) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Information',
        text: 'Patient ID and Doctor ID are required'
      });
      return;
    }

    setLoading(true);
    
    try {
      const submitData = {
        ...historyData,
        PatientId: patientId,
        DoctorId: doctorId
      };

      let response;
      if (existingHistoryId) {
        // Update existing history
        submitData.Id = existingHistoryId;
        response = await updatePatientHistory(submitData);
      } else {
        // Save new history
        response = await savePatientHistory(submitData);
      }

      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: response.message,
          timer: 2000,
          showConfirmButton: false
        });
        
        // If it was a new record, set the ID for future updates
        if (!existingHistoryId && response.data && response.data.Id) {
          setExistingHistoryId(response.data.Id);
        }

        // Close modal after successful save
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: response.message || 'Failed to save history'
        });
      }
    } catch (error) {
      console.error('Error submitting history:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An unexpected error occurred'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setHistoryData({
      PastSurgicalhistory: '',
      PastMedicalhistory: '',
      Drughistory: '',
      GyneorOBShistory: ''
    });
  };

  if (!show) return null;

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fas fa-history me-2"></i>
              Patient Medical History
              {existingHistoryId && <small className="text-muted ms-2">(Existing Record)</small>}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={loading}
            ></button>
          </div>

          <div className="modal-body">
            {initialLoad ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading patient history...</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="PastSurgicalhistory" className="form-label">
                      Past Surgical History
                    </label>
                    <textarea
                      className="form-control"
                      id="PastSurgicalhistory"
                      name="PastSurgicalhistory"
                      rows="4"
                      value={historyData.PastSurgicalhistory}
                      onChange={handleInputChange}
                      placeholder="Enter past surgical history..."
                    />
                  </div>
                  
                  <div className="col-md-6 mb-3">
                    <label htmlFor="PastMedicalhistory" className="form-label">
                      Past Medical History
                    </label>
                    <textarea
                      className="form-control"
                      id="PastMedicalhistory"
                      name="PastMedicalhistory"
                      rows="4"
                      value={historyData.PastMedicalhistory}
                      onChange={handleInputChange}
                      placeholder="Enter past medical history..."
                    />
                  </div>
                  
                  <div className="col-md-6 mb-3">
                    <label htmlFor="Drughistory" className="form-label">
                      Drug History
                    </label>
                    <textarea
                      className="form-control"
                      id="Drughistory"
                      name="Drughistory"
                      rows="4"
                      value={historyData.Drughistory}
                      onChange={handleInputChange}
                      placeholder="Enter drug history..."
                    />
                  </div>
                  
                  <div className="col-md-6 mb-3">
                    <label htmlFor="GyneorOBShistory" className="form-label">
                      Gynecological/OBS History
                    </label>
                    <textarea
                      className="form-control"
                      id="GyneorOBShistory"
                      name="GyneorOBShistory"
                      rows="4"
                      value={historyData.GyneorOBShistory}
                      onChange={handleInputChange}
                      placeholder="Enter gynecological/OBS history..."
                    />
                  </div>
                </div>
              </form>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              <i className="fas fa-times me-1"></i>
              Close
            </button>
            
            {!initialLoad && (
              <>
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={handleClear}
                  disabled={loading}
                >
                  <i className="fas fa-eraser me-1"></i>
                  Clear
                </button>
                
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      {existingHistoryId ? 'Updating...' : 'Saving...'}
                    </>
                  ) : (
                    <>
                      <i className={`fas ${existingHistoryId ? 'fa-edit' : 'fa-save'} me-1`}></i>
                      {existingHistoryId ? 'Update History' : 'Save History'}
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientHistoryModal;